
import { format } from "d3-format";
import { timeFormat } from "d3-time-format";

import React from "react";

import PropTypes from "prop-types";
import { Chart, ChartCanvas } from "./component/react-stockcharts";

import {
	BarSeries,
	OHLCSeries,
	LineSeries,
  MACDSeries,
  AreaSeries,
  CandlestickSeries,
  SARSeries,
  BollingerSeries,
  StochasticSeries,
  AlternatingFillAreaSeries,
  StraightLine,
  ElderRaySeries,
  RSISeries,
} from "./component/react-stockcharts/lib/series";
import { XAxis, YAxis } from "./component/react-stockcharts/lib/axes";
import {
	CrossHairCursor,
	CurrentCoordinate,
	MouseCoordinateX,
  MouseCoordinateY,
  EdgeIndicator,
  PriceCoordinate,
} from "./component/react-stockcharts/lib/coordinates";

import { discontinuousTimeScaleProviderBuilder } from "./component/react-stockcharts/lib/scale";
import { OHLCTooltip, MovingAverageTooltip, HoverTooltip, MACDTooltip, SingleValueTooltip, BollingerBandTooltip, StochasticTooltip, RSITooltip} from "./component/react-stockcharts/lib/tooltip";
import { sma,ema, macd, change, elderImpulse, tma, sar, bollingerBand, atr, rsi, stochasticOscillator, forceIndex, elderRay } from "./component/react-stockcharts/lib/indicator";
import { fitWidth } from "./component/react-stockcharts/lib/helper";
import { InteractiveText, InteractiveYCoordinate, TrendLine, DrawingObjectSelector, FibonacciRetracement, ClickCallback } from "./component/react-stockcharts/lib/interactive";
import {  head, last, toObject } from "./component/react-stockcharts/lib/utils";
import { getMorePropsForChart } from "react-stockcharts/lib/interactive/utils";
import shortid from "shortid";

import {
    saveInteractiveNodes,
    getInteractiveNodes,
    getSelected
} from "./utils/interactiveutils";

const elder = elderRay();

const dateFormat = timeFormat("%Y-%m-%d");
const numberFormat = format(".2f");

const stoAppearance = {
	stroke: Object.assign({},
		StochasticSeries.defaultProps.stroke)
};

function getMaxUndefined(calculators) {
	return calculators.map(each => each.undefinedLength()).reduce((a, b) => Math.max(a, b));
}



const bbStroke = {
  top: "#00ff00",
  middle: "#0000ff",
  bottom: "#ff0000",
};
const bbFill = "#d1d1d1";

const s1 = {
	...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate,
	stroke: "#FF0000",
	textFill: "#FF0000",
	text: "S1",
	edge: {
		...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate.edge,
        strokeOpacity: 0,
        fillOpacity: 0,
	}
};
const s2 = {
	...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate,
	stroke: "#FF0000",
	textFill: "#FF0000",
	text: "S2",
	edge: {
		...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate.edge,
        strokeOpacity: 0,
        fillOpacity: 0,
        
	}
};
const r1 = {
	...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate,
	stroke: "#0000FF",
	textFill: "#0000FF",
  text: "R1",
  id: shortid.generate(),
  yValue: 20,
	edge: {
		...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate.edge,
		strokeOpacity: 0,
	}
};
const r2 = {
	...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate,
	stroke: "#0000FF",
	textFill: "#0000FF",
  text: "R2",
  id: shortid.generate(),
  yValue: -12,
	edge: {
		...InteractiveYCoordinate.defaultProps.defaultPriceCoordinate.edge,
		strokeOpacity: 0,
	}
};

const fi = forceIndex()
			.merge((d, c) => {d.fi = c;})
			.accessor(d => d.fi);

const fiEMA13 = ema()
  .id(1)
  .options({ windowSize: 13, sourcePath: "fi" })
  .merge((d, c) => {d.fiEMA13 = c;})
  .accessor(d => d.fiEMA13);
      
const coordinateList_3 = [r1, r2];
const ema12 = ema()
			.id(1)
			.options({ windowSize: 12 })
			.merge((d, c) => { d.ema12 = c; })
			.accessor(d => d.ema12);

const macdCalculator = macd()
  .options({
    fast: 12,
    slow: 26,
    signal: 9,
  })
  .merge((d, c) => { d.macd = c; })
  .accessor(d => d.macd);

const fullSTO = stochasticOscillator()
  .options({ windowSize: 14, kWindowSize: 3, dWindowSize: 4 })
  .merge((d, c) => {d.fullSTO = c;})
  .accessor(d => d.fullSTO);

const elderImpulseCalculator = elderImpulse()
  .macdSource(macdCalculator.accessor())
  .emaSource(ema12.accessor());
const rsiCalculator = rsi()
  .options({ windowSize: 14 })
  .merge((d, c) => {d.rsi = c;})
  .accessor(d => d.rsi);
const atrCalculator = atr()
  .options({ windowSize: 14 })
  .merge((d, c) => {d.atr14 = c;})
  .accessor(d => d.atr14);
const changeCalculator = change();

const accelerationFactor = .02;
const maxAccelerationFactor = .2;

const sarCalculator = sar()
  .options({
    accelerationFactor, maxAccelerationFactor
  })
  .merge((d, c) => {d.sar = c;})
  .accessor(d => d.sar);

const bbCalculator = bollingerBand()
  .merge((d, c) => {d.bb = c;})
  .accessor(d => d.bb);

function tooltipContent(ys) {
	return ({ currentItem, xAccessor }) => {
        
		return {
			x: dateFormat(xAccessor(currentItem)),
			y: [
				{
					label: "open",
					value: currentItem.open && numberFormat(currentItem.open)
				},
				{
					label: "high",
					value: currentItem.high && numberFormat(currentItem.high)
				},
				{
					label: "low",
					value: currentItem.low && numberFormat(currentItem.low)
				},
				{
					label: currentItem.label === undefined ? "close" : currentItem.label,
					value: currentItem.close && numberFormat(currentItem.close)
				}
			]
				.concat(
					ys.map(each => {
                        if(each.value(currentItem) < currentItem.high * 10 && currentItem.close != 0)
                            return {
                                label: each.label,
                                value: each.value(currentItem),
                                stroke: each.stroke
                            }
					    })
				)
		};
	};
}

function tooltipContentForTMTI(ys) {
	return ({ currentItem, xAccessor }) => {
		return {
			x: dateFormat(xAccessor(currentItem)),
			y: [
				{
					label: "open",
					value: currentItem.open && numberFormat(currentItem.open)
				},
				{
					label: "high",
					value: currentItem.high && numberFormat(currentItem.high)
				},
				{
					label: "low",
					value: currentItem.low && numberFormat(currentItem.low)
				},
				{
					label: "close",
					value: currentItem.close && numberFormat(currentItem.close)
				}
			]
				.concat(
					ys.map(each => {
            if(currentItem.mri !== undefined)
            return {
              label: each.label,
              value: currentItem.mri,
              stroke: each.stroke
            }
					})
				)
		};
	};
}
function tooltipContentForStochastic() {
	return ({ currentItem, xAccessor }) => {
    if(currentItem.fullSTO === undefined)
      return {
        x: dateFormat(xAccessor(currentItem)),
        y: [
          {
            label: "open",
            value: currentItem.open && numberFormat(currentItem.open)
          },
          {
            label: "high",
            value: currentItem.high && numberFormat(currentItem.high)
          },
          {
            label: "low",
            value: currentItem.low && numberFormat(currentItem.low)
          },
          {
            label: "close",
            value: currentItem.close && numberFormat(currentItem.close)
          }
        ]
      };
		return {
			x: dateFormat(xAccessor(currentItem)),
			y: [
				{
					label: "open",
					value: currentItem.open && numberFormat(currentItem.open)
				},
				{
					label: "high",
					value: currentItem.high && numberFormat(currentItem.high)
				},
				{
					label: "low",
					value: currentItem.low && numberFormat(currentItem.low)
				},
				{
					label: "close",
					value: currentItem.close && numberFormat(currentItem.close)
				}
			]
				.concat(
					[{
              label: "K",
              value: numberFormat(currentItem.fullSTO.K),
              stroke: '#A6E45B',
          },
          {
            label: "D",
            value: numberFormat(currentItem.fullSTO.D),
            stroke: '#C846C8',
          }]
				)
		};
	};
}
function tooltipContentForForceIndex() {
	return ({ currentItem, xAccessor }) => {
    if(currentItem.fullSTO === undefined)
      return {
        x: dateFormat(xAccessor(currentItem)),
        y: [
          {
            label: "open",
            value: currentItem.open && numberFormat(currentItem.open)
          },
          {
            label: "high",
            value: currentItem.high && numberFormat(currentItem.high)
          },
          {
            label: "low",
            value: currentItem.low && numberFormat(currentItem.low)
          },
          {
            label: "close",
            value: currentItem.close && numberFormat(currentItem.close)
          }
        ]
      };
		return {
			x: dateFormat(xAccessor(currentItem)),
			y: [
				{
					label: "open",
					value: currentItem.open && numberFormat(currentItem.open)
				},
				{
					label: "high",
					value: currentItem.high && numberFormat(currentItem.high)
				},
				{
					label: "low",
					value: currentItem.low && numberFormat(currentItem.low)
				},
				{
					label: "close",
					value: currentItem.close && numberFormat(currentItem.close)
				}
			]
				.concat(
					[{
              label: "FI",
              value: numberFormat(currentItem.fi),
              stroke: '#699AC3',
          },
          {
            label: "FIEMA13",
            value: numberFormat(currentItem.fiEMA13),
            stroke: currentItem.fiEMA13 < 0 ? '#E43935' : '#38C172',
          }]
				)
		};
	};
}

function tooltipContentForElderray() {
	return ({ currentItem, xAccessor }) => {
    if(currentItem.fullSTO === undefined)
      return {
        x: dateFormat(xAccessor(currentItem)),
        y: [
          {
            label: "open",
            value: currentItem.open && numberFormat(currentItem.open)
          },
          {
            label: "high",
            value: currentItem.high && numberFormat(currentItem.high)
          },
          {
            label: "low",
            value: currentItem.low && numberFormat(currentItem.low)
          },
          {
            label: "close",
            value: currentItem.close && numberFormat(currentItem.close)
          }
        ]
      };
		return {
			x: dateFormat(xAccessor(currentItem)),
			y: [
				{
					label: "open",
					value: currentItem.open && numberFormat(currentItem.open)
				},
				{
					label: "high",
					value: currentItem.high && numberFormat(currentItem.high)
				},
				{
					label: "low",
					value: currentItem.low && numberFormat(currentItem.low)
				},
				{
					label: "close",
					value: currentItem.close && numberFormat(currentItem.close)
				}
			]
				.concat(
					[{
              label: "BULL",
              value: numberFormat(currentItem.elderRay.bullPower),
              stroke: '#8DBBA0',
          },
          {
            label: "BEAR",
            value: numberFormat(currentItem.elderRay.bearPower),
            stroke: '#FF2B2B',
          }]
				)
		};
	};
}

function tooltipContentForMACD() {
	return ({ currentItem, xAccessor }) => {
    if(currentItem.macd.macd === undefined || currentItem.macd.divergence === undefined || currentItem.macd.signal === undefined)
      return {
        x: dateFormat(xAccessor(currentItem)),
        y: [
          {
            label: "open",
            value: currentItem.open && numberFormat(currentItem.open)
          },
          {
            label: "high",
            value: currentItem.high && numberFormat(currentItem.high)
          },
          {
            label: "low",
            value: currentItem.low && numberFormat(currentItem.low)
          },
          {
            label: "close",
            value: currentItem.close && numberFormat(currentItem.close)
          }
        ]
      };
		return {
			x: dateFormat(xAccessor(currentItem)),
			y: [
				{
					label: "open",
					value: currentItem.open && numberFormat(currentItem.open)
				},
				{
					label: "high",
					value: currentItem.high && numberFormat(currentItem.high)
				},
				{
					label: "low",
					value: currentItem.low && numberFormat(currentItem.low)
				},
				{
					label: "close",
					value: currentItem.close && numberFormat(currentItem.close)
				}
			]
				.concat(
					[{
              label: "MACD",
              value: numberFormat(currentItem.macd.macd),
              stroke: '#ff0000',
          },
          {
            label: 'SIGNAL',
            value: numberFormat(currentItem.macd.signal),
            stroke: '#50F750',
          },
          {
            label: 'DIVERGENCE',
            value: numberFormat(currentItem.macd.divergence),
            stroke: '#6597C1',
          }]
				)
		};
	};
}

function mobilecheck() {
    var check = false;
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
    return check;
};

function tooltipContentForRsiAtr() {
	return ({ currentItem, xAccessor }) => {
    if(currentItem.rsi === undefined || currentItem.atr14 === undefined)
      return {
        x: dateFormat(xAccessor(currentItem)),
        y: [
          {
            label: "open",
            value: currentItem.open && numberFormat(currentItem.open)
          },
          {
            label: "high",
            value: currentItem.high && numberFormat(currentItem.high)
          },
          {
            label: "low",
            value: currentItem.low && numberFormat(currentItem.low)
          },
          {
            label: "close",
            value: currentItem.close && numberFormat(currentItem.close)
          }
        ]
      };
		return {
			x: dateFormat(xAccessor(currentItem)),
			y: [
				{
					label: "open",
					value: currentItem.open && numberFormat(currentItem.open)
				},
				{
					label: "high",
					value: currentItem.high && numberFormat(currentItem.high)
				},
				{
					label: "low",
					value: currentItem.low && numberFormat(currentItem.low)
				},
				{
					label: "close",
					value: currentItem.close && numberFormat(currentItem.close)
				}
			]
				.concat(
					[{
              label: "RSI",
              value: numberFormat(currentItem.rsi),
              stroke: '#C846C8',
          },
          {
            label: 'ATR',
            value: numberFormat(currentItem.atr14),
            stroke: '#FFA14E',
          }]
				)
		};
	};
}




const dis = {
  "GAB": 0.15,
  "AMZN": 50,
  "AAPL": 8,
  "INTC": 1,
  "DIA": 5,
}

const macdAppearance = {
	stroke: {
		macd: "#FF0000",
		signal: "#00F300",
	},
	fill: {
		divergence: "#4682B4"
	},
};

let edgeValue = 0, todayClosePrice = 0;

var topValue, bottomValue;

const sma50 = sma()
    .options({ windowSize: 50 })
    .merge((d, c) => {d.sma50 = c;})
    .accessor(d => d.sma50)
    .stroke("red");

const sma200 = sma()
    .options({ windowSize: 200 })
    .merge((d, c) => {d.sma200 = c;})
    .accessor(d => d.sma200)
    .stroke("blue");
const sma20 = sma()
    .options({ windowSize: 20 })
    .merge((d, c) => {d.sma20 = c;})
    .accessor(d => d.sma20)
const tma20 = tma()
    .options({ windowSize: 20 })
    .merge((d, c) => {d.tma20 = c;})
    .accessor(d => d.tma20)
    .stroke("green");
const ema20 = ema()
    .options({ windowSize: 20 })
    .merge((d, c) => {d.ema20 = c;})
    .accessor(d => d.ema20)
    .stroke("#ED39D7");
const ema50 = ema()
    .options({ windowSize: 50 })
    .merge((d, c) => {d.ema50 = c;})
    .accessor(d => d.ema50)
    .stroke("#39EDDF");
const smaVolume50 = sma()
    .id(3)
    .options({
        windowSize: 50,
        sourcePath: "volume",
    })
    .merge((d, c) => {d.smaVolume50 = c;})
    .accessor(d => d.smaVolume50);

class CustomizedStockChart extends React.Component {
	constructor(props) {
        super(props);
        const { data: inputData, additionalData, width } = props;
        this.isDrag = false;
        

        const maxWindowSize = getMaxUndefined([
            sma50,
            sma200,
            smaVolume50,
        ]);
        topValue = 0;
        bottomValue = 1000000000;
        inputData.map((data, index) => {
        topValue = topValue < data.high ? data.high : topValue;
        bottomValue = bottomValue > data.low ? data.low : bottomValue;
        if(index == 0)
            data.before = 1000000;
        else
            data.before = inputData[index - 1].close;
        })

        edgeValue = inputData[inputData.length - 1].mri;
        todayClosePrice = inputData[inputData.length - 1].close;

        const dataToCalculate = inputData;
        
        const calculatedData = elder(fiEMA13(fi(fullSTO(macdCalculator(atrCalculator(rsiCalculator(bbCalculator(sarCalculator(ema50(ema20(tma20(sma20(sma50(sma200(smaVolume50(elderImpulseCalculator(ema12(changeCalculator(dataToCalculate)))))))))))))))))));
            const indexCalculator = discontinuousTimeScaleProviderBuilder().inputDateAccessor(d => d.date).initialIndex(0).indexCalculator();
            const { index } = indexCalculator(calculatedData);
        

        const xScaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor(d => d.date)
        .withIndex(index);
        
        const { data: linearData, xScale, xAccessor, displayXAccessor } = xScaleProvider(calculatedData);
        
        var textlist = [];
        
        /* adding asterisk to the chart */
        if(additionalData !== undefined){
        linearData.map((data, index) => {
        var offset = (topValue - bottomValue) * 0.03, interval = offset, overDuplicate = 0, underDuplicate = 0;
        for(var i = 0; i< additionalData.marks.length;i ++)
        {
            const mark = additionalData.marks[i];
            if(mark.date.getTime() === data.date.getTime())
            {
            var textitem = {
                text:mark.text,
                bgFill: mark.bgcolor,
                textFill: mark.color,
                hoverText: {
                text: mark.mouseover,
                color: mark.color,
                bgHeight: mark.color === "#000000" ? (mark.text.length === 1 ? 90 : 90) : mark.text.length === 1 && mark.text !== "*" ? 90 : 90,
                bgWidth: 140,
                bgFill: '#ffffff',
                bgStroke: '#cecece',
                },
                date: mark.date
            }
            
            if(mark.direction === "over"){
                overDuplicate ++;
                offset = interval * overDuplicate * 1.3;
                textitem.position = [index, data.high + offset];
            }else{
                underDuplicate ++;
                offset = interval * underDuplicate;
                textitem.position = [index, data.low - offset * 2];
            }
                
            textlist.push(textitem);
            }
        }
        })

        /* adding asterisk to the chart */

        /* adding R1, R2, S1, S2 to the chart */
        var coordinate = [];
        if(additionalData.R1 != null){
        coordinate.push(
            {
            ...r1,
            yValue: additionalData.R1,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }
        if(additionalData.R2 != null){
        coordinate.push(
            {
            ...r2,
            yValue: additionalData.R2,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }
        if(additionalData.S1 != null){
        coordinate.push(
            {
            ...s1,
            yValue: additionalData.S1,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }
        if(additionalData.S2 != null){
        coordinate.push(
            {
            ...s2,
            yValue: additionalData.S2,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }
        /* adding R1, R2, S1, S2 to the chart */
        }

            this.state = {
                sma20,
                sma50,
                sma200,
                smaVolume50,
                tma20,
                ema20,
                ema50,
                trends_1: [],
                retracements_1: [],
                data: linearData,
                length: xAccessor(linearData[0]),
                        xScale,
                xAccessor,
                displayXAccessor,
                textList_1: textlist,
                hoveredInteractiveText: false,
                yCoordinateList_1 : coordinate,
                suffix: 1
        };
        this.saveInteractiveNodes = saveInteractiveNodes.bind(this);
        this.getInteractiveNodes = getInteractiveNodes.bind(this);
        this.handleDownloadMore = this.handleDownloadMore.bind(this);
        this.handleHoverInteractiveText = this.handleHoverInteractiveText.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleReset = this.handleReset.bind(this);
    }

    CandleColor = (d) => {
        if(d.close === 0 )
            return '#DEDEDE';
        if(this.props.trend === false){
            if(this.props.chartType === 'ohlc')
                return '#000000';
            if(this.props.chartType === 'candlestick')
                {
                    return d.close > d.open ? "#6BA583" : "#FF0000";
                }
        }

        if (((d.close > d.sma20) && (d.close <= d.sma50)) || 
            ((d.close <= d.sma20) && (d.close > d.sma50)) || 
            ((d.close < d.sma20) && (d.close >= d.sma50)) ||
            ((d.close >= d.sma20) && (d.close < d.sma50)))
          return '#0000FF'; //blue
      
        if ((d.close > d.sma20) && (d.close > d.sma50))
          return '#00FF00'; //green
      
        if ((d.close < d.sma20) && (d.close < d.sma50))
          return '#FF0000'; //red
        return '#FF0000'; //red
      }

    componentWillReceiveProps(nextProps){

        const {additionalData} = nextProps;
        const {data: linearData} = this.state;
        let { data: inputData } = this.props;
        var textlist = [];
        if(linearData === undefined)
            return;
        if(additionalData === undefined)
            return;

            const calculatedData = elder(fiEMA13(fi(fullSTO(macdCalculator(atrCalculator(rsiCalculator(bbCalculator(sarCalculator(ema50(ema20(tma20(sma20(sma50(sma200(smaVolume50(elderImpulseCalculator(ema12(changeCalculator(inputData)))))))))))))))))));
            const indexCalculator = discontinuousTimeScaleProviderBuilder().inputDateAccessor(d => d.date).initialIndex(0).indexCalculator();
            const { index } = indexCalculator(calculatedData);
        

            const xScaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor(d => d.date)
            .withIndex(index);
            
            let { data: newData } = xScaleProvider(calculatedData);

            this.setState({data: newData});
        
        
        linearData.map((data, index) => {
        var offset = (topValue - bottomValue) * 0.03, interval = offset, overDuplicate = 0, underDuplicate = 0;
        for(var i = 0; i< additionalData.marks.length;i ++)
        {
            const mark = additionalData.marks[i];
            if(mark.date.getTime() === data.date.getTime())
            {
            var textitem = {
                text:mark.text,
                bgFill: mark.bgcolor,
                textFill: mark.color,
                hoverText: {
                    text: mark.mouseover,
                    color: mark.color,
                    /* bgHeight: mark.color === "#000000" ? (mark.text.length === 1 ? 90 : 90) : mark.text.length === 1 && mark.text !== "*" ? 90 : 90, */
                    bgWidth: 140,
                    bgFill: '#ffffff',
                    bgStroke: '#cecece',
                },
                date: mark.date
            }
            
            if(mark.direction === "over"){
                overDuplicate ++;
                offset = interval * overDuplicate * 1.3;
                textitem.position = [index, data.high + offset];
            }else{
                underDuplicate ++;
                offset = interval * underDuplicate;
                textitem.position = [index, data.low - offset * 2];
            }

            textlist.push(textitem);
            }
        }
        })

        /* adding asterisk to the chart */

        /* adding R1, R2, S1, S2 to the chart */
        
        var coordinate = [];
        if(additionalData.R1 != null){
        coordinate.push(
            {
            ...r1,
            yValue: additionalData.R1,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }
        if(additionalData.R2 != null){
        coordinate.push(
            {
            ...r2,
            yValue: additionalData.R2,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }
        if(additionalData.S1 != null){
        coordinate.push(
            {
            ...s1,
            yValue: additionalData.S1,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }
        if(additionalData.S2 != null){
        coordinate.push(
            {
            ...s2,
            yValue: additionalData.S2,
            id: shortid.generate(),
            draggable: false,
            }
        )
        }

        let trends_1 = [...this.state.trends_1], retracements_1 = [...this.state.retracements_1];
        if(nextProps.enableTrendLine === false)
            trends_1 = [];
        if(nextProps.enableFib === false)
            retracements_1 = [];


        this.setState({
            textList_1: textlist,
            yCoordinateList_1 : coordinate,
            trends_1,
            retracements_1
        });
    }
    handleDelete(yCoordinate, moreProps) {
            this.setState(state => {
                const key = 'yCoordinateList_1';

                const list = state[key];
                return {
                    [key]: list.filter(d => d.id !== yCoordinate.id)
                };
            });
    }
    
    handleHoverInteractiveText(hovering){
        this.setState({hoveredInteractiveText: hovering});
    }

    handleReset() {
        this.setState({
            suffix: this.state.suffix + 1
        });
    }
    saveCanvasNode = (node) => {
        this.canvasNode = node;
        window.chart = node;
	}
        handleDownloadMore(start, end) {
            if (Math.ceil(start) === end) {
        return;
        }
            
            const { data: prevData, sma50, sma200, smaVolume50, sma20, tma20, ema20, ema50 } = this.state;
            const { data: inputData, additionalData } = this.props;

            if (inputData.length === prevData.length) {
        return;
        }

            const rowsToDownload = end - Math.ceil(start);

            const maxWindowSize = getMaxUndefined([
                sma50,
                sma200,
                smaVolume50
            ]);

        
            const dataToCalculate = inputData
        .slice(-rowsToDownload - maxWindowSize - prevData.length, - prevData.length);
        const calculatedData = elder(fiEMA13(fi(fullSTO(macdCalculator(atrCalculator(rsiCalculator(bbCalculator(sarCalculator(ema50(ema20(tma20(sma20(sma50(sma200(smaVolume50(elderImpulseCalculator(ema12(changeCalculator(dataToCalculate)))))))))))))))))));
            const indexCalculator = discontinuousTimeScaleProviderBuilder()
                .initialIndex(Math.ceil(start))
                .indexCalculator();
            const { index } = indexCalculator(
                calculatedData
                    .slice(-rowsToDownload)
                    .concat([...prevData]));

            const xScaleProvider = discontinuousTimeScaleProviderBuilder()
                .initialIndex(Math.ceil(start))
                .withIndex(index);
        const { data: linearData, xScale, xAccessor, displayXAccessor } = xScaleProvider(calculatedData.slice(-rowsToDownload).concat([...prevData]));
        
        /* adding asterisk to the chart */
        var textlist = [];
        if(additionalData !== undefined){
        linearData.forEach((data, index) => {
        var offset = (topValue - bottomValue) * 0.03, interval = offset, overDuplicate = 0, underDuplicate = 0;
        for(var i = 0; i< additionalData.marks.length;i ++)
        {
            const mark = additionalData.marks[i];
            if(mark.date.getTime() === data.date.getTime())
            {
            var pos = xAccessor(data);
            var textitem = {
                text:mark.text,
                bgFill: mark.bgcolor,
                textFill: mark.color,
                fontWeight: 'bold',
                bgStroke: mark.color,
                hoverText: {
                    text: mark.mouseover,
                    color: mark.color,
                    /* bgHeight: mark.color === "#000000" ? (mark.text.length === 1 ? 70 : 90) : mark.text.length === 1 && mark.text !== "*" ? 70 : 90, */
                    bgWidth: 140,
                    bgFill: '#ffffff',
                    bgStroke: '#cecece',
                },
                date: mark.date
            }
            
            if(mark.direction === "over"){
                overDuplicate ++;
                offset = interval * overDuplicate * 1.3;
                textitem.position = [index, data.high + offset];
            }else{
                underDuplicate ++;
                offset = interval * underDuplicate;
                textitem.position = [index, data.low - offset * 2];
            }
            textlist.push(textitem);
            }
        }
        })
    }
        /* adding asterisk to the chart */
    }
    
    extentsCalculator = (d) =>{
        const {stockType} = this.props;
        const offset = (topValue - bottomValue) * 0.1;
        return [d.high + offset, d.low - offset];
    }

    onDrawCompleteChart1 = (trends_1) => {
        
		this.setState({
			enableTrendLine: true,
			trends_1
		});
    }

    onFibComplete1 = (retracements_1) => {
		this.setState({
			retracements_1,
			enableFib: false
		});
	}

    renderYCoordinateEdgeValues = () => {
        
        if(this.state.yCoordinateList_1 !== undefined)
        return this.state.yCoordinateList_1.map((coordinate, key) => 
        <EdgeIndicator itemType="last" orient="right" edgeAt="right" yAccessor={d => coordinate.yValue} fill={d => "#6BA583"} key={key} />)
        return null;
    }

    handleSelect = () => {
        
    }

    handleSelection = (interactives, moreProps, e) => {
        
        /* var selected = interactives[0].objects.find(interactive => {
            return interactive.selected;
        }); 
        selected = undefined;
        if(selected === undefined)
        { */
            /* console.log(moreProps);
            console.log(moreProps.currentItem.date); */
            this.props.handleSelectAsterisk(moreProps);    
            return;

        /* }
        this.props.handleSelectAsterisk(selected); */
    }

    renderCrossHairs = () => {
        if(this.props.crossHairs == null)
            return;
        return[
            <StraightLine yValue={this.props.crossHairs.yValue} type="horizontal" key="1" strokeDasharray="ShortDash" stroke="#868686"/>,
            <StraightLine xValue={this.props.crossHairs.xValue} type="vertical" key="2" strokeDasharray="ShortDash" stroke="#868686"/>,
        ]
    }

    handleMouseMove = (moreProps, e) => {
        if(!mobilecheck())
            return;

        if(e.type == 'touchmove')    
        {
            return;
        }
        this.props.handleSelectAsterisk(moreProps);   
    }

	render() {
		const { type, width, ratio, indicator1, indicator2} = this.props;
		const { data, sma50, sma200, smaVolume50, xScale, xAccessor, displayXAccessor, tma20, ema20, ema50 } = this.state;
        const start = xAccessor(last(data));
        const end = xAccessor(data[Math.max(0, data.length - this.props.duration)]);
        let chartHeight = this.props.chartHeight;
        // if(indicator2 === 'forceindex' || indicator2 === 'rsiatr')
        // chartHeight = 700;
        const gridHeight = chartHeight;
        const gridWidth = width - 160;
        const yGrid = { innerTickSize: -1 * gridWidth, tickStrokeOpacity: 0.05 };
        const xGrid = { innerTickSize: -1 * gridHeight, tickStrokeOpacity: 0.05 };
                let tooltip = [], hoverTooltip = {};
        // eslint-disable-next-line default-case
        switch(indicator1){
        case 'sma50_sma200':
        tooltip = [{
            yAccessor: sma50.accessor(),
            type: sma50.type(),
            stroke: sma50.stroke(),
            ...sma50.options(),
        },{
            yAccessor: sma200.accessor(),
            type: sma200.type(),
            stroke: sma200.stroke(),
            ...sma200.options(),
        }];
        hoverTooltip = [{
            label: `${sma50.type()}(${sma50.options()
            .windowSize})`,
            value: d => numberFormat(sma50.accessor()(d)),
            stroke: sma50.stroke()
        },
        {
            label: `${sma200.type()}(${sma200.options()
            .windowSize})`,
            value: d => numberFormat(sma200.accessor()(d)),
            stroke: sma200.stroke()
        }
        ];
        break;
        case 'tma20':
        tooltip = [{
            yAccessor: tma20.accessor(),
            type: tma20.type(),
            stroke: tma20.stroke(),
            ...tma20.options(),
        }]
        hoverTooltip = [{
            label: `${tma20.type()}(${tma20.options()
            .windowSize})`,
            value: d => numberFormat(tma20.accessor()(d)),
            stroke: tma20.stroke()
        }];
        break;
        case 'ema20':
        tooltip = [{
            yAccessor: ema20.accessor(),
            type: ema20.type(),
            stroke: ema20.stroke(),
            ...ema20.options(),
        }]
        hoverTooltip = [{
            label: `${ema20.type()}(${ema20.options()
            .windowSize})`,
            value: d => numberFormat(ema20.accessor()(d)),
            stroke: ema20.stroke()
        }];
        break;
        case 'ema50':
        tooltip = [{
            yAccessor: ema50.accessor(),
            type: ema50.type(),
            stroke: ema50.stroke(),
            ...ema50.options(),
        }]
        hoverTooltip = [{
            label: `${ema50.type()}(${ema50.options()
            .windowSize})`,
            value: d => numberFormat(ema50.accessor()(d)),
            stroke: ema50.stroke()
        }];
        break;
        case 'sar':
        tooltip = [{
            label: 'SAR(0.02, 0.2)',
            yAccessor: sarCalculator.accessor(),
            stroke: '#0000ff'
        }]
        hoverTooltip = [{
            label: 'SAR(0.02, 0.2)',
            value: d => numberFormat(sarCalculator.accessor()(d)),
            stroke: '#0000ff'
        }];
        break;
        case 'bollingerband':
        hoverTooltip = [{
            label: 'Top',
            value: d => numberFormat(bbCalculator.accessor()(d).top),
            stroke: '#00ff00'
            },
            {
            label: 'Middle',
            value: d => numberFormat(bbCalculator.accessor()(d).middle),
            stroke: '#0000ff'
            },
            {
            label: 'Bottom',
            value: d => numberFormat(bbCalculator.accessor()(d).bottom),
            stroke: '#ff0000'
            }];
        break;
        case 'none' :
            tooltip = [{
                yAccessor: sma50.accessor(),
                type: sma50.type(),
                //stroke: sma50.stroke(),
                ...sma50.options(),
            },{
                yAccessor: sma200.accessor(),
                type: sma200.type(),
                //stroke: sma200.stroke(),
                ...sma200.options(),
            }];
            hoverTooltip = [{
                label: `${sma50.type()}(${sma50.options()
                .windowSize})`,
                value: d => numberFormat(sma50.accessor()(d)),
                //stroke: sma50.stroke()
            },
            {
                label: `${sma200.type()}(${sma200.options()
                .windowSize})`,
                value: d => numberFormat(sma200.accessor()(d)),
                //stroke: sma200.stroke()
            }
            ];
        break;
        }
		return (
			<ChartCanvas ratio={ratio} width={width} height={chartHeight}
                margin={{ left: 20, right: 65, top: 60, bottom: 30 }} type={type}
                data={data}
                xScale={xScale}
                xAccessor={xAccessor}
                displayXAccessor={displayXAccessor}
                seriesName={`MSFT_${this.state.suffix}`}
                xExtents={[start + 1, end]}
                onLoadMore={this.handleDownloadMore}
                origin={[0, 0]}
                //ref={ref => window.chart = ref}
                ref={this.saveCanvasNode}
            >
				<Chart id={1}
                    yExtents={[d => this.extentsCalculator(d), d => sma50.accessor(), d => sma200.accessor()]}
                    padding={{ top: 30, bottom: 10}}
                    height={Math.floor(chartHeight/1.75)}
                    origin={[0, 0]}
                    >

					<YAxis axisAt="right" orient="right" ticks={5} {...yGrid}/>
					<MouseCoordinateY
						at="right"
						orient="right"
						displayFormat={format(".2f")} />
                    {

                        this.renderCrossHairs()

                    }
                    <EdgeIndicator itemType="last" orient="right" edgeAt="right" yAccessor={d => d.close} fill={d => d.close > d.before ? "#6BA583" : "#E3342F"} />
                    {
                        indicator1 === 'sma50_sma200' && 
                        <LineSeries yAccessor={sma50.accessor()} stroke={sma50.stroke()}/>
                    }
                    {
                        indicator1 === 'sma50_sma200' && 
                        <LineSeries yAccessor={sma200.accessor()} stroke={sma200.stroke()}/>
                    }
                                {
                        indicator1 === 'tma20' && 
                        <LineSeries yAccessor={tma20.accessor()} stroke={tma20.stroke()}/>
                    }
                    {
                        indicator1 === 'ema20' && 
                        <LineSeries yAccessor={ema20.accessor()} stroke={ema20.stroke()}/>
                    }
                    {
                        indicator1 === 'ema50' && 
                        <LineSeries yAccessor={ema50.accessor()} stroke={ema50.stroke()}/>
                    }
                    {
                        indicator1 === 'sar' && 
                        <SARSeries yAccessor={d => d.sar}/>
                    }
                    {
                        indicator1 === 'bollingerband' &&
                        <BollingerSeries yAccessor={d => d.bb} 	stroke={bbStroke} fill={bbFill} />
                    }
                    {
                        indicator1 === 'bollingerband' &&
                        <BollingerBandTooltip
                            origin={[0, -20]}
                            yAccessor={d => d.bb}
                            options={bbCalculator.options()} />
                    }
                                
                    {
                        
                        this.props.chartType === "ohlc" && 
                        <OHLCSeries 
                        stroke={ d => this.CandleColor(d)}
                        />
                    }
                    
                    { 
                        this.props.chartType === "candlestick" && <CandlestickSeries stroke={d => this.CandleColor(d)} fill={d => this.CandleColor(d)} wickStroke={d => this.CandleColor(d)}/>
                    }
                    
                                {
                        
                        indicator1 === "sma50_sma200" && 
                        <CurrentCoordinate yAccessor={sma50.accessor()} fill={sma50.stroke()} />
                    }
                                {
                        
                        indicator1 === "sma50_sma200" && 
                        <CurrentCoordinate yAccessor={sma200.accessor()} fill={sma200.stroke()} />
                    }
                    {
                        
                        indicator1 === "tma20" && 
                        <CurrentCoordinate yAccessor={tma20.accessor()} fill={tma20.stroke()} />
                    }
                    {
                        
                        indicator1 === "ema20" && 
                        <CurrentCoordinate yAccessor={ema20.accessor()} fill={ema20.stroke()} />
                    }
                    {
                        
                        indicator1 === "ema50" && 
                        <CurrentCoordinate yAccessor={ema50.accessor()} fill={ema50.stroke()} />
                    }
                    { 
                        indicator1 === "bollingerband" && 
                        <CurrentCoordinate yAccessor={d => bbCalculator.accessor()(d).top} fill='#00ff00' />
                    }
                    { 
                        indicator1 === "bollingerband" && 
                        <CurrentCoordinate yAccessor={d => bbCalculator.accessor()(d).middle} fill='#0000ff' />
                    }
                    { 
                        indicator1 === "bollingerband" && 
                        <CurrentCoordinate yAccessor={d => bbCalculator.accessor()(d).bottom} fill='#ff0000' />
                    }
                        <OHLCTooltip origin={[73, -40]}/>
                                {
                        indicator1 !== 'bollingerband' &&
                        <MovingAverageTooltip
                        
                        origin={[0, -25]}
                        options={[
                            ...tooltip,
                        ]}
                        />
                    }

                    <InteractiveText
                        ref={this.saveInteractiveNodes("InteractiveText", 1)}
                        enabled={true}
                        text="Lorem ipsum..."
                        textList={this.state.textList_1}
                        hovering={hovering => this.handleHoverInteractiveText(hovering)}
                        handleSelectAsterisk={this.props.handleSelectAsterisk}
                        onSelect={this.handleSelect}
                    />
                    {
                        this.props.sr == true &&
                        <InteractiveYCoordinate
                            ref={this.saveInteractiveNodes("InteractiveYCoordinate", 1)}
                            enabled={true}
                            onDragComplete={this.onDragComplete}
                            onDelete={this.handleDelete}
                            yCoordinateList={this.state.yCoordinateList_1}
                        />
                    }
                    
          
                    <HoverTooltip
                        handleSelectAsterisk={this.props.handleSelectAsterisk}
                        yAccessor={sma50.accessor()}
                        tooltipContent={tooltipContent([
                        ...hoverTooltip
                        ])}
                        tooltipContentForTMTI={tooltipContentForTMTI([{
                        label: 'TMTI',
                        stroke: "red" 
                        }])}
                        tooltipContentForMACD={tooltipContentForMACD()}
                        tooltipContentForRsiAtr={tooltipContentForRsiAtr()}
                        tooltipContentForStochastic={tooltipContentForStochastic()}
                        tooltipContentForForceIndex={tooltipContentForForceIndex()}
                        tooltipContentForElderray={tooltipContentForElderray()}
                        fontSize={15}
                        hoveringInteractiveText={this.state.hoveredInteractiveText}
                        indicator2={indicator2}
                    />

                    <TrendLine
						ref={this.saveInteractiveNodes("Trendline", 1)}
						enabled={this.props.enableTrendLine}
						type="RAY"
						snap={false}
						snapTo={d => [d.high, d.low]}
						onComplete={this.onDrawCompleteChart1}
						trends={this.state.trends_1}
					/>
                    <FibonacciRetracement
						ref={this.saveInteractiveNodes("FibonacciRetracement", 1)}
						enabled={this.props.enableFib}
						retracements={this.state.retracements_1}
						onComplete={this.onFibComplete1}
					/>
                    {/* <DrawingObjectSelector
						enabled
						getInteractiveNodes={this.getInteractiveNodes}
						drawingObjectMap={{
							InteractiveText: "textList"
						}}
                        onSelect={this.handleSelection}
					/> */}
                    <ClickCallback
                        onDoubleClick={ (moreProps, e) => { this.props.handleSelectAsterisk(moreProps); } }
                        onPan={ (moreProps, e) => { this.handleMouseMove(moreProps, e) } }
                    />
                </Chart>
                <Chart id={2}
                    yExtents={[d => d.volume, smaVolume50.accessor()]}
                    height={Math.floor(chartHeight/7)} origin={(w, h) => [0, Math.floor(chartHeight/1.75)]}
                >
                    <YAxis axisAt="right" orient="right" ticks={5} tickFormat={format(".2s")}/>
                    
                    <MouseCoordinateY
                        at="left"
                        orient="left"
                        displayFormat={format(".4s")} />

                    <BarSeries yAccessor={d => d.volume} fill={d => d.close > d.before ? "#6BA583" : "#E3342F"} data={data}/>
                    <AreaSeries yAccessor={smaVolume50.accessor()} stroke={smaVolume50.stroke()} fill={smaVolume50.fill()}/>
                    <CurrentCoordinate yAccessor={smaVolume50.accessor()} fill={smaVolume50.stroke()} />
				</Chart>
                {/* [-70, 70] */}
                {
                indicator2 === 'tmti' &&
                <Chart id={3} height={Math.floor(chartHeight/7)}
                    yExtents={d => [70, -70]}
                    origin={(w, h) => [0, h - Math.floor(chartHeight/7)]} padding={{ top: 10, bottom: 10 }}
                        >					
                    <XAxis axisAt="bottom" orient="bottom" ticks={width / 80} {...xGrid}/>
                    <YAxis axisAt="right" orient="right" ticks={5}/>
                    <MouseCoordinateX
                    at="bottom"
                    orient="bottom"
                    displayFormat={timeFormat("%Y-%m-%d")} />

                    <EdgeIndicator itemType="last" orient="right" edgeAt="right" yAccessor={d => d.mri} fill={d => "#6BA583"}/>
                    <LineSeries yAccessor={d => d.mri} stroke="#000000"/>
                    <PriceCoordinate
                        at="left"
                        orient="left"
                        price={50}
                        lineOpacity={0.5}
                        displayFormat={format(".2f")}
                        text="BEARISH"
                        stroke="#000000"
                        lineStroke="#000000"
                        fill="#FFFFFF"
                        textFill="#000000"
                        dx={60}
                    />
                    <PriceCoordinate
                        at="left"
                        orient="left"
                        price={-50}
                        lineOpacity={0.5}
                        displayFormat={format(".2f")}
                        text="BULLISH"
                        stroke="#000000"
                        lineStroke="#000000"
                        fill="#FFFFFF"
                        textFill="#000000"
                        dx={58}
                    />
                    <PriceCoordinate
                        at="left"
                        orient="left"
                        price={-30}
                        displayFormat={format(".2f")}
                        strokeDasharray="ShortDash"
                    />
                    <PriceCoordinate
                        at="left"
                        orient="left"
                        price={30}
                        displayFormat={format(".2f")}
                        strokeDasharray="ShortDash"
                    />
                    <SingleValueTooltip
                        yAccessor={d => d.mri}
                        yLabel="TOTTY MARKET TIMING INDICATOR"
                        yDisplayFormat={format(".4s")}
                        origin={[0, 8]}
                    />
                </Chart>
                }
                {
                indicator2 === 'rsiatr' &&
                <Chart id={3} height={Math.floor(chartHeight/14)}
                    yExtents={[0, 100]}
                    origin={(w, h) => [0, h - 100]} padding={{ top: 10, bottom: 10 }}
                        >					
                    <YAxis axisAt="right" orient="right" ticks={3}/>
                    <RSISeries yAccessor={d => d.rsi} />
                    <RSITooltip origin={[0, 15]}
                    yAccessor={d => d.rsi}
                    options={rsiCalculator.options()} />
                </Chart>
                }
                {
                indicator2 === 'rsiatr' &&
                <Chart id={4} height={Math.floor(chartHeight/14)}
                    yExtents={atrCalculator.accessor()}
                    origin={(w, h) => [0, h - Math.floor(chartHeight/14)]} padding={{ top: 10, bottom: 10 }}
                        >					
                    <XAxis axisAt="bottom" orient="bottom" ticks={width / 80} {...xGrid}/>
                    <YAxis axisAt="right" orient="right" ticks={3}/>
                    <MouseCoordinateX
                    at="bottom"
                    orient="bottom"
                    displayFormat={timeFormat("%Y-%m-%d")} />
                    <LineSeries yAccessor={atrCalculator.accessor()} stroke={atrCalculator.stroke()}/>
                    <SingleValueTooltip
                    yAccessor={atrCalculator.accessor()}
                    yLabel={`ATR (${atrCalculator.options().windowSize})`}
                    yDisplayFormat={format(".2f")}
                    /* valueStroke={atr14.stroke()} - optional prop */
                    /* labelStroke="#4682B4" - optional prop */
                    origin={[0, 15]}/>
                </Chart>
                }
                {
                indicator2 === 'macd' &&
                <Chart id={3} height={Math.floor(chartHeight/7)}
                    yExtents={macdCalculator.accessor()}
                    origin={(w, h) => [0, h - Math.floor(chartHeight/7)]} padding={{ top: 10, bottom: 10 }}
                        >					
                    <XAxis axisAt="bottom" orient="bottom" ticks={width / 80} {...xGrid}/>
                    <YAxis axisAt="right" orient="right" ticks={5}/>
                    <MouseCoordinateX
                    at="bottom"
                    orient="bottom"
                    displayFormat={timeFormat("%Y-%m-%d")} />
                    <MACDSeries yAccessor={d => d.macd} {...macdAppearance} />
                    <MACDTooltip
                    origin={[0, 15]}
                    yAccessor={d => d.macd}
                    options={macdCalculator.options()}
                    appearance={macdAppearance}
                            />
                
                </Chart>
                }
                {
                indicator2 === 'stochastic' &&
                <Chart id={3} height={Math.floor(chartHeight/7)}
                    yExtents={[0, 100]}
                    origin={(w, h) => [0, h - Math.floor(chartHeight/7)]} padding={{ top: 10, bottom: 10 }}
                >
                    
                    <XAxis axisAt="bottom" orient="bottom" ticks={width / 80} {...xGrid}/>
                    <YAxis axisAt="right" orient="right" ticks={5}/>
            
                    <MouseCoordinateX
                    at="bottom"
                    orient="bottom"
                    displayFormat={timeFormat("%Y-%m-%d")} />
                    <StochasticSeries
                    yAccessor={d => d.fullSTO}
                    {...stoAppearance} />
                    <StochasticTooltip
                    origin={[0, 15]}
                    yAccessor={d => d.fullSTO}
                    options={fullSTO.options()}
                    appearance={stoAppearance}
                    label="Full STO" />
                </Chart> 
                }
                {
                indicator2 === 'forceindex' &&
                <Chart id={3} height={Math.floor(chartHeight/14)}
                    yExtents={fi.accessor()}
                    origin={(w, h) => [0, h - Math.floor(chartHeight/7)]} padding={{ top: 10, bottom: 10 }}
                >
                    <YAxis axisAt="right" orient="right" ticks={3} tickFormat={format(".2s")}/>
                    <AreaSeries baseAt={scale => scale(0)} yAccessor={fi.accessor()} />
                    <StraightLine yValue={0} />
                    <SingleValueTooltip
                    yAccessor={fi.accessor()}
                    yLabel="ForceIndex (1)"
                    yDisplayFormat={format(".4s")}
                    origin={[0, 15]}
                    />
                </Chart>
                }
                {
                indicator2 === 'forceindex' &&
                <Chart id={4} height={Math.floor(chartHeight/14)}
                    yExtents={fiEMA13.accessor()}
                    origin={(w, h) => [0, h - Math.floor(chartHeight/14)]} padding={{ top: 10, bottom: 10 }}
                >

                    <XAxis axisAt="bottom" orient="bottom" ticks={width / 80} {...xGrid}/>
                    <YAxis axisAt="right" orient="right" ticks={3} tickFormat={format(".2s")}/>
                    <AlternatingFillAreaSeries
                    baseAt={0}
                    yAccessor={fiEMA13.accessor()}
                    />
                    
                    <StraightLine yValue={0} />
                    <MouseCoordinateX
                    at="bottom"
                    orient="bottom"
                    displayFormat={timeFormat("%Y-%m-%d")} />
                    <SingleValueTooltip
                    yAccessor={fiEMA13.accessor()}
                    yLabel={`ForceIndex (${fiEMA13.options().windowSize})`}
                    yDisplayFormat={format(".4s")}
                    origin={[0, 15]}
                    />
                </Chart> 
                }
                {
                indicator2 === 'elderray' && 
                <Chart id={3} height={Math.floor(chartHeight/7)}
                    yExtents={[0, elder.accessor()]}
                    origin={(w, h) => [0, h - Math.floor(chartHeight/7)]}
                    padding={{ top: 10, bottom: 10 }}
                    >
                    <XAxis axisAt="bottom" orient="bottom" showTicks={false} outerTickSize={0} {...xGrid} />
                    <YAxis axisAt="right" orient="right" ticks={4} tickFormat={format(".2f")}/>

                    <MouseCoordinateY
                        at="right"
                        orient="right"
                        displayFormat={format(".2f")} />

                    <ElderRaySeries yAccessor={elder.accessor()} />
                    <SingleValueTooltip
                        yAccessor={elder.accessor()}
                        yLabel="Elder Ray"
                        yDisplayFormat={d => `${format(".2f")(d.bullPower)}, ${format(".2f")(d.bearPower)}`}
                        origin={[0, 15]}/>
                </Chart>
                }
				<CrossHairCursor />
			</ChartCanvas>
		);
	}
}

CustomizedStockChart.propTypes = {
	data: PropTypes.array.isRequired,
	width: PropTypes.number.isRequired,
	ratio: PropTypes.number.isRequired,
	type: PropTypes.oneOf(["svg", "hybrid"]).isRequired,
};

CustomizedStockChart.defaultProps = {
	type: "svg",
};

CustomizedStockChart = fitWidth(CustomizedStockChart);

export default CustomizedStockChart;
